import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Calendar, User, Mail, Truck, FileText, ShieldAlert, CheckCircle, Edit, Package, DollarSign, Scale, ClipboardCheck, Image, XCircle, Download } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Authorized approvers - same structure as FormLogPage
const APPROVERS = {
  // Regular Timesheet approvers
  'b.deering@rogerstrucking.ca': { initials: 'BD', name: 'B. Deering', role: 'admin', forms: ['timesheet'] },
  'j.creamer@rogerstrucking.ca': { initials: 'JC', name: 'J. Creamer', role: 'ar', forms: ['timesheet', 'shop'] },
  // Shop Timesheet approvers
  'lring@rogerstrucking.ca': { initials: 'LR', name: 'L. Ring', role: 'admin', forms: ['shop'] }
};

export default function FormViewPage() {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submission, setSubmission] = useState(null);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUserEmail, setCurrentUserEmail] = useState('');

  useEffect(() => {
    fetchSubmission();
    // Get current user email
    const token = localStorage.getItem('vaultai_token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUserEmail(payload.email?.toLowerCase() || '');
      } catch (e) {
        console.error('Failed to decode token:', e);
      }
    }
  }, [submissionId]);

  const fetchSubmission = async () => {
    try {
      const token = localStorage.getItem('vaultai_token');
      const headers = { Authorization: `Bearer ${token}` };

      const subRes = await axios.get(`${API}/forms/submissions/${submissionId}`, { headers });
      setSubmission(subRes.data);

      // Fetch template for field labels
      if (subRes.data.template_id) {
        const templateRes = await axios.get(`${API}/forms/templates/${subRes.data.template_id}`, { headers });
        setTemplate(templateRes.data);
      }
    } catch (err) {
      console.error('Failed to fetch submission:', err);
      toast.error('Failed to load submission');
      navigate('/dashboard/forms');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    // Display in BC (Pacific) timezone since the team works there
    return new Date(dateStr).toLocaleString('en-CA', {
      timeZone: 'America/Vancouver',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };
  
  // Handle approval
  const handleApprove = async (approvalType) => {
    try {
      const token = localStorage.getItem('vaultai_token');
      await axios.post(
        `${API}/forms/submissions/${submissionId}/approve?approval_type=${approvalType}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${approvalType.toUpperCase()} approval added`);
      fetchSubmission(); // Refresh data
    } catch (err) {
      console.error('Approval failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };
  
  // Handle revoke approval
  const handleRevokeApproval = async (approvalType) => {
    try {
      const token = localStorage.getItem('vaultai_token');
      await axios.delete(
        `${API}/forms/submissions/${submissionId}/approve?approval_type=${approvalType}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${approvalType.toUpperCase()} approval revoked`);
      fetchSubmission(); // Refresh data
    } catch (err) {
      console.error('Revoke failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to revoke approval');
    }
  };

  // Time-off approve / deny (admin only)
  const handleTimeOffDecision = async (decision) => {
    try {
      const token = localStorage.getItem('vaultai_token');
      await axios.post(
        `${API}/forms/submissions/${submissionId}/time-off-decision?decision=${decision}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Time-off request ${decision === 'approve' ? 'approved' : 'denied'}`);
      fetchSubmission();
    } catch (err) {
      console.error('Time-off decision failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to update time-off request');
    }
  };

  // Export as PDF
  const handleExportPDF = async () => {
    try {
      const token = localStorage.getItem('vaultai_token');
      const response = await axios.get(
        `${API}/forms/submissions/${submissionId}/pdf`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `form_${submissionId.slice(0, 8)}.pdf`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully');
    } catch (err) {
      console.error('PDF export failed:', err);
      toast.error('Failed to export PDF');
    }
  };

  const getOptionLabel = (field, value) => {
    if (field?.options) {
      const option = field.options.find(o => o.value === value);
      return option?.label || value;
    }
    return value;
  };

  const renderFieldValue = (field, value) => {
    if (value === null || value === undefined || value === '') return <span className="text-muted-foreground">-</span>;
    
    // Handle arrays (multiselect/checkbox)
    if (Array.isArray(value)) {
      if (value.length === 0) return <span className="text-muted-foreground">-</span>;
      return (
        <div className="flex flex-wrap gap-2">
          {value.map((v, i) => (
            <span key={i} className="px-2 py-1 bg-primary/10 text-primary rounded text-sm">
              {getOptionLabel(field, v)}
            </span>
          ))}
        </div>
      );
    }
    
    // Handle image fields - download link only (use relative URL to avoid CSS hiding rules)
    if (field?.type === 'image' && value) {
      const token = localStorage.getItem('vaultai_token');
      const label = field.label || (field.id === 'receipt_photo' ? 'Scale Receipt Photo' : 'Photo');
      return (
        <a 
          href={`/api/forms/images/${value}?token=${token}`}
          download
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
          style={{ display: 'inline-flex' }}
        >
          <Image className="h-4 w-4" />
          Download {label}
        </a>
      );
    }
    
    // Handle select/combobox
    if (field?.type === 'select' || field?.type === 'combobox') {
      return getOptionLabel(field, String(value));
    }
    
    // Handle weight fields - format with unit from label (tonnes or KG)
    if (field?.id?.includes('weight')) {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        // Check if label specifies tonnes or KG
        const label = field?.label?.toLowerCase() || '';
        const unit = label.includes('tonnes') ? 't' : (label.includes('kg') ? 'KG' : 't');
        return `${num.toLocaleString()} ${unit}`;
      }
    }
    
    // Handle currency fields
    if (field?.id?.includes('rate') || field?.id?.includes('amount') || field?.id?.includes('total') || 
        field?.id?.includes('subtotal') || field?.id?.includes('pst') || field?.id?.includes('gst')) {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
    }
    
    return String(value);
  };

  const getSectionIcon = (sectionId, category) => {
    const id = sectionId?.toLowerCase() || '';
    const cat = category?.toLowerCase() || '';
    
    if (id.includes('shipment') || id.includes('equipment')) return <Truck className="h-5 w-5" />;
    if (id.includes('charge') || id.includes('customer')) return <User className="h-5 w-5" />;
    if (id.includes('cargo') || id.includes('particulars')) return <Package className="h-5 w-5" />;
    if (id.includes('weight')) return <Scale className="h-5 w-5" />;
    if (id.includes('financial') || id.includes('summary')) return <DollarSign className="h-5 w-5" />;
    if (id.includes('acknowledgment') || id.includes('receipt')) return <ClipboardCheck className="h-5 w-5" />;
    if (id.includes('photo')) return <Image className="h-5 w-5" />;
    if (id.includes('hazard') || id.includes('safety') || cat === 'safety') return <ShieldAlert className="h-5 w-5" />;
    return <FileText className="h-5 w-5" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Submission not found</p>
        <Link to="/dashboard/forms">
          <Button variant="outline" className="mt-4">Back to Forms</Button>
        </Link>
      </div>
    );
  }

  const data = submission.data || {};
  const isLogistics = template?.category?.toLowerCase() === 'logistics';
  const isOperations = template?.category?.toLowerCase() === 'operations';
  const isBOL = template?.name?.toLowerCase().includes('bill of lading');
  const isScaleReceipt = template?.name?.toLowerCase().includes('scale receipt');
  const templateNameLower = template?.name?.toLowerCase() || '';
  const isTimesheet = templateNameLower.includes('timesheet') || templateNameLower.includes('time sheet');
  const isShopTimesheet = templateNameLower.includes('shop') && isTimesheet;
  const isTimeOff = template?.name?.toLowerCase().includes('time-off');
  const isSafety = template?.category?.toLowerCase() === 'safety';
  
  // Get form type for approver checking
  const getFormType = () => {
    if (isShopTimesheet) return 'shop';
    if (isTimesheet) return 'timesheet';
    return null;
  };
  
  // Get the admin approver info text for this form type
  const getAdminApproverText = () => {
    if (isShopTimesheet) return 'Only LR (lring@rogerstrucking.ca) can approve';
    return 'Only BD (b.deering@rogerstrucking.ca) can approve';
  };
  
  // Check if current user can approve for this form type
  const canApproveForm = (type) => {
    const userEmailLower = currentUserEmail?.toLowerCase() || '';
    const approver = APPROVERS[userEmailLower];
    if (!approver) return false;
    if (approver.role !== type) return false;
    const formType = getFormType();
    if (!formType) return false;
    return approver.forms?.includes(formType);
  };
  
  // Determine the category display name
  const getCategoryDisplay = () => {
    const cat = template?.category?.toLowerCase() || '';
    if (cat === 'logistics') return 'Logistics';
    if (cat === 'operations') return 'Operations';
    if (cat === 'safety') return 'Safety';
    if (cat === 'hr') return 'HR';
    return template?.category || 'Form';
  };
  
  // Determine the back link based on form type
  const getBackLink = () => {
    if (isScaleReceipt) return '/dashboard/scale-receipts';
    if (templateNameLower.includes('clearsky')) return '/dashboard/clearsky-log';
    if (template?.id) return `/dashboard/forms/log/${template.id}`;
    return '/dashboard/forms/submissions';
  };

  return (
    <div className="space-y-6" data-testid="form-view-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to={getBackLink()}>
            <Button variant="outline" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{submission.template_name}</h1>
            <p className="text-muted-foreground">
              {getCategoryDisplay()} Submission Details
              {isBOL && data.bol_number && ` • BOL #${data.bol_number}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isTimeOff && user?.role === 'admin' && (
            <>
              <Button
                variant="outline"
                className={`border-green-500/40 ${submission.time_off_status === 'approved' ? 'bg-green-500/20 text-green-400' : 'text-green-400 hover:bg-green-500/10'}`}
                onClick={() => handleTimeOffDecision('approve')}
                data-testid="time-off-approve-btn"
              >
                {submission.time_off_status === 'approved' ? 'Approved ✓' : 'Approve'}
              </Button>
              <Button
                variant="outline"
                className={`border-red-500/40 ${submission.time_off_status === 'denied' ? 'bg-red-500/20 text-red-400' : 'text-red-400 hover:bg-red-500/10'}`}
                onClick={() => handleTimeOffDecision('deny')}
                data-testid="time-off-deny-btn"
              >
                {submission.time_off_status === 'denied' ? 'Denied ✗' : 'Deny'}
              </Button>
            </>
          )}
          <Button variant="outline" onClick={handleExportPDF} data-testid="export-pdf-btn">
            <Download className="h-4 w-4 mr-2" />
            Export PDF
          </Button>
          <Link to={`/dashboard/forms/edit/${submissionId}`}>
            <Button variant="outline">
              <Edit className="h-4 w-4 mr-2" />
              Edit Submission
            </Button>
          </Link>
        </div>
      </div>

      {/* Meta Info */}
      <Card className={isLogistics ? 'border-blue-500/30' : isOperations ? 'border-orange-500/30' : 'border-yellow-500/30'}>
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-3">
              <User className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Submitted By</p>
                <p className="font-medium">{submission.submitted_by}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Email</p>
                <p className="font-medium text-sm">{submission.submitted_by_email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Submitted</p>
                <p className="font-medium text-sm">{formatDate(submission.submitted_at)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className={`h-5 w-5 ${submission.is_draft ? 'text-amber-500' : 'text-green-500'}`} />
              <div>
                <p className="text-xs text-muted-foreground">Status</p>
                <p className="font-medium">{submission.is_draft ? 'Draft' : 'Completed'}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Timesheet Approvals */}
      {isTimesheet && (
        <Card className="border-purple-500/30 bg-purple-500/5">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-purple-400">
              <ClipboardCheck className="h-5 w-5" />
              Approvals
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-6">
              {/* Admin Approval */}
              <div className="p-4 rounded-lg bg-background/50 border">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <ShieldAlert className="h-4 w-4" />
                  Admin Approval
                </h4>
                {submission.approval_admin?.approved ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-green-600 text-white">Approved</Badge>
                      <span className="font-medium">{submission.approval_admin.approved_by}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(submission.approval_admin.approved_at)}
                    </p>
                    {canApproveForm('admin') && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-2 text-red-500 hover:text-red-700"
                        onClick={() => handleRevokeApproval('admin')}
                      >
                        <XCircle className="h-3 w-3 mr-1" />
                        Revoke Approval
                      </Button>
                    )}
                  </div>
                ) : (
                  <div>
                    <p className="text-muted-foreground text-sm mb-2">Not yet approved</p>
                    {canApproveForm('admin') ? (
                      <Button 
                        onClick={() => handleApprove('admin')}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Approve as Admin
                      </Button>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        {getAdminApproverText()}
                      </p>
                    )}
                  </div>
                )}
              </div>
              
              {/* AR Approval */}
              <div className="p-4 rounded-lg bg-background/50 border">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  AR Approval
                </h4>
                {submission.approval_ar?.approved ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-600 text-white">Approved</Badge>
                      <span className="font-medium">{submission.approval_ar.approved_by}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(submission.approval_ar.approved_at)}
                    </p>
                    {canApproveForm('ar') && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-2 text-red-500 hover:text-red-700"
                        onClick={() => handleRevokeApproval('ar')}
                      >
                        <XCircle className="h-3 w-3 mr-1" />
                        Revoke Approval
                      </Button>
                    )}
                  </div>
                ) : (
                  <div>
                    <p className="text-muted-foreground text-sm mb-2">Not yet approved</p>
                    {canApproveForm('ar') ? (
                      <Button 
                        onClick={() => handleApprove('ar')}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Approve as AR
                      </Button>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        Only JC (j.creamer@rogerstrucking.ca) can approve
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* BOL Quick Summary (for logistics forms) */}
      {isBOL && (
        <Card className="border-blue-500/30 bg-blue-500/5">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-blue-400">
              <Truck className="h-5 w-5" />
              BOL Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">BOL #</p>
                <p className="font-bold text-lg">{data.bol_number || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Customer</p>
                <p className="font-bold text-lg">{data.charge_to_name || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Truck #</p>
                <p className="font-bold text-lg">{data.truck_number || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Driver</p>
                <p className="font-bold text-lg">{data.driver_name || '-'}</p>
              </div>
              {data.net_weight && (
                <div>
                  <p className="text-xs text-muted-foreground">Net Weight</p>
                  <p className="font-bold text-lg text-blue-400">{parseFloat(data.net_weight).toLocaleString()} KG</p>
                </div>
              )}
              {data.total && (
                <div>
                  <p className="text-xs text-muted-foreground">Total</p>
                  <p className="font-bold text-lg text-green-400">${parseFloat(data.total).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                </div>
              )}
              <div className="md:col-span-2">
                <p className="text-xs text-muted-foreground">Location (LSD)</p>
                <p className="font-bold">{data.charge_to_location || '-'}</p>
              </div>
            </div>
            {/* Download BOL Photo Button */}
            {data.bol_photo && (
              <div className="mt-4 pt-4 border-t border-blue-500/20">
                <a 
                  href={`/api/forms/images/${data.bol_photo}?token=${localStorage.getItem('vaultai_token')}`}
                  download={`BOL-${data.bol_number || 'photo'}.jpg`}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
                >
                  <Image className="h-4 w-4" />
                  Download BOL Photo
                </a>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Scale Receipt Quick Summary */}
      {isScaleReceipt && (
        <Card className="border-cyan-500/30 bg-cyan-500/5">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-cyan-400">
              <Scale className="h-5 w-5" />
              Scale Receipt Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Date</p>
                <p className="font-bold text-lg">{data.date || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Generator</p>
                <p className="font-bold text-lg">{data.generator_name || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Truck #</p>
                <p className="font-bold text-lg">{data.truck_unit || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">BOL #</p>
                <p className="font-bold text-lg">{data.bol_number || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Gross Weight</p>
                <p className="font-bold text-lg">{data.gross_weight ? `${parseFloat(data.gross_weight).toLocaleString()} t` : '-'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Tare Weight</p>
                <p className="font-bold text-lg">{data.tare_weight ? `${parseFloat(data.tare_weight).toLocaleString()} t` : '-'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Net Weight</p>
                <p className="font-bold text-lg text-green-400">{data.net_weight ? `${parseFloat(data.net_weight).toLocaleString()} t` : '-'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Facility</p>
                <p className="font-bold">{data.facility_name || '-'}</p>
              </div>
            </div>
            {/* Download Scale Receipt Photo Button */}
            {data.receipt_photo && (
              <div className="mt-4 pt-4 border-t border-cyan-500/20">
                <a 
                  href={`/api/forms/images/${data.receipt_photo}?token=${localStorage.getItem('vaultai_token')}`}
                  download={`ScaleReceipt-${data.date || 'photo'}.jpg`}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm transition-colors"
                >
                  <Image className="h-4 w-4" />
                  Download Scale Receipt Photo
                </a>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dynamic Sections from Template */}
      {template?.sections?.sort((a, b) => a.order - b.order).map((section) => {
        // Check if section has any data
        const sectionHasData = section.fields?.some(field => {
          const value = data[field.id];
          return value !== null && value !== undefined && value !== '' && 
                 (!Array.isArray(value) || value.length > 0);
        });

        if (!sectionHasData) return null;

        return (
          <Card key={section.id} className={isLogistics ? 'border-blue-500/20' : 'border-yellow-500/20'}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                {getSectionIcon(section.id, template.category)}
                {section.title}
              </CardTitle>
              {section.description && (
                <p className="text-sm text-muted-foreground">{section.description}</p>
              )}
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {section.fields?.sort((a, b) => a.order - b.order).map((field) => {
                  const value = data[field.id];
                  // Skip empty values
                  if (value === null || value === undefined || value === '' || 
                      (Array.isArray(value) && value.length === 0)) {
                    return null;
                  }
                  
                  // Full width for textarea and image fields
                  const isFullWidth = field.type === 'textarea' || field.type === 'image';
                  
                  return (
                    <div key={field.id} className={isFullWidth ? 'md:col-span-2' : ''}>
                      <p className="text-sm text-muted-foreground">{field.label}</p>
                      <div className="font-medium mt-1">
                        {renderFieldValue(field, value)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Back Button */}
      <div className="flex justify-center pt-4">
        <Link to={getBackLink()}>
          <Button variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to {template?.name || 'Forms'} Log
          </Button>
        </Link>
      </div>
    </div>
  );
}
